// AI-CONTEXT: See docs/features/arbeidsvaardigheden.md

// De balk staat op een middenas: rechts van het midden is vooruitgang, links
// achteruitgang. De schaal volgt de grootste uitslag in de set, met een
// ondergrens van één Likert-punt. Zonder die ondergrens zou een set waarin
// niemand meer dan 0,2 punt beweegt er even indrukwekkend uitzien als een set
// vol hele treden.
const ONDERGRENS_SCHAAL = 1

const getal = new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
})

const tekenKlasse = (groei) => (groei > 0 ? 'pos' : groei < 0 ? 'neg' : 'neutraal')

/**
 * @typedef {object} GroeiRij
 * @property {string} id - Effect-id uit de standaard (EFF-01 t/m EFF-09)
 * @property {string} naam - Naam van het thema
 * @property {number|null} groei - Verschil in Likert-punten, of null als er geen twee metingen zijn
 * @property {string} [meta] - Regel onder de naam (bijv. '3,0 → 4,3' of '12 deelnemers')
 */

/**
 * De negen thema's van MSI Arbeidsvaardigheden met hun groei, als lijst met een
 * balk op een middenas. Wordt gebruikt voor één deelnemer (tabblad
 * Arbeidsvaardigheden) en voor de organisatie als geheel (dashboardkaart).
 *
 * Een thema zonder tweede meting toont een streepje en geen balk. Een 0 zou daar
 * lezen als "niet vooruitgegaan", terwijl er niets gemeten is - en een gemeten 0
 * moet daar juist wel van te onderscheiden zijn.
 *
 * @param {object} props
 * @param {string} [props.titel] - Kop boven de lijst; weglaten waar de omgeving al een kop heeft
 * @param {Array<GroeiRij>} props.rijen - Eén rij per thema, in de volgorde van de standaard
 * @param {string} [props.toelichting] - Regel onder de kop
 * @param {string} [props.eenheid] - Achtervoegsel achter elke waarde (bijv. 'punt')
 */
const GroeiPerThema = ({ titel, rijen = [], toelichting, eenheid }) => {
    const grootste = rijen.reduce(
        (max, r) => (r.groei === null ? max : Math.max(max, Math.abs(r.groei))),
        ONDERGRENS_SCHAAL
    )

    return (
        <section className="av-groei">
            {(titel || toelichting) && (
                <header className="av-groei-kop">
                    {titel && <h3>{titel}</h3>}
                    {toelichting && <span className="av-groei-toelichting">{toelichting}</span>}
                </header>
            )}

            <ul className="av-groei-lijst">
                {rijen.map((r) => (
                    <li key={r.id} className="av-groei-rij">
                        <div className="av-groei-naam">
                            <span>{r.naam}</span>
                            {r.meta && <span className="av-groei-meta">{r.meta}</span>}
                        </div>

                        {/* Twee helften om de middenas: links achteruitgang, rechts
                            vooruitgang. De balkbreedte is een percentage van zijn eigen
                            helft, zodat de grootste uitslag precies de helft vult. */}
                        <div className="av-groei-spoor" aria-hidden="true">
                            <div className="av-groei-helft links">
                                {r.groei !== null && r.groei < 0 && (
                                    <span
                                        className="av-groei-balk neg"
                                        style={{ width: `${(Math.abs(r.groei) / grootste) * 100}%` }}
                                    />
                                )}
                            </div>
                            <span className="av-groei-as" />
                            <div className="av-groei-helft rechts">
                                {r.groei !== null && r.groei >= 0 && (
                                    <span
                                        className={`av-groei-balk ${tekenKlasse(r.groei)}`}
                                        style={{ width: `${(r.groei / grootste) * 100}%` }}
                                    />
                                )}
                            </div>
                        </div>

                        <span className={`av-groei-waarde ${r.groei === null ? 'leeg' : tekenKlasse(r.groei)}`}>
                            {r.groei === null ? '—' : getal.format(r.groei)}
                            {r.groei !== null && eenheid ? ` ${eenheid}` : ''}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    )
}

export default GroeiPerThema
